import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Code,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  HStack,
  Divider,
} from '@chakra-ui/react';

function ApiDocs() {
  const endpoints = [
    {
      category: 'Authentication',
      items: [
        {
          method: 'POST',
          path: '/api/v1/auth/login/',
          description: 'Authenticate and get a token',
          body: '{ "username": "admin", "password": "password" }',
          response: '{ "token": "your-auth-token" }',
        },
      ],
    },
    {
      category: 'Public API',
      items: [
        {
          method: 'GET',
          path: '/api/v1/ping',
          description: 'Health check endpoint',
          response: '{ "application": "marketing-brevo", "success": true }',
        },
        {
          method: 'POST',
          path: '/api/v1/message',
          description: 'Send a transactional email',
          headers: 'X-API-KEY: your-api-key',
          body: '{ "from": "sender@example.com", "to": "recipient@example.com", "title": "Subject", "text_content": "Text body", "html_content": "<p>HTML body</p>" }',
        },
        {
          method: 'POST',
          path: '/api/v1/smsmessage',
          description: 'Send a transactional SMS',
          headers: 'X-API-KEY: your-api-key',
          body: '{ "from": "+1234567890", "to": "+0987654321", "text": "SMS content" }',
        },
        {
          method: 'POST',
          path: '/api/v1/accountinfo',
          description: 'Create or update a marketing contact',
          headers: 'X-API-KEY: your-api-key',
          body: 'email, first_name, last_name, phone, sms, external_id, tags (form data)',
        },
        {
          method: 'POST',
          path: '/api/v1/syncbrevo',
          description: 'Sync contacts to Brevo',
          headers: 'X-API-KEY: your-api-key',
        },
        {
          method: 'GET',
          path: '/api/v1/state',
          description: 'Get current state (contacts and sync runs)',
          headers: 'X-API-KEY: your-api-key',
        },
      ],
    },
    {
      category: 'Contacts (Admin)',
      items: [
        {
          method: 'GET',
          path: '/api/v1/contacts/',
          description: 'List all contacts',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'POST',
          path: '/api/v1/contacts/',
          description: 'Create a new contact',
          headers: 'Authorization: Token your-token',
          body: '{ "email": "contact@example.com", "first_name": "John", "last_name": "Doe" }',
        },
        {
          method: 'GET',
          path: '/api/v1/contacts/{id}/',
          description: 'Get contact details',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'PUT',
          path: '/api/v1/contacts/{id}/',
          description: 'Update a contact',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'DELETE',
          path: '/api/v1/contacts/{id}/',
          description: 'Delete a contact',
          headers: 'Authorization: Token your-token',
        },
      ],
    },
    {
      category: 'API Keys (Admin)',
      items: [
        {
          method: 'GET',
          path: '/api/v1/api-keys/',
          description: 'List all API keys',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'POST',
          path: '/api/v1/api-keys/',
          description: 'Create a new API key',
          headers: 'Authorization: Token your-token',
          body: '{ "name": "Key Name", "description": "Description" }',
        },
        {
          method: 'PUT',
          path: '/api/v1/api-keys/{id}/',
          description: 'Update an API key',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'DELETE',
          path: '/api/v1/api-keys/{id}/',
          description: 'Delete an API key',
          headers: 'Authorization: Token your-token',
        },
      ],
    },
    {
      category: 'Configurations (Admin)',
      items: [
        {
          method: 'GET',
          path: '/api/v1/configurations/',
          description: 'List all configurations',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'POST',
          path: '/api/v1/configurations/',
          description: 'Create a new configuration',
          headers: 'Authorization: Token your-token',
          body: '{ "key": "CONFIG_KEY", "value": "config value" }',
        },
        {
          method: 'PUT',
          path: '/api/v1/configurations/{id}/',
          description: 'Update a configuration',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'DELETE',
          path: '/api/v1/configurations/{id}/',
          description: 'Delete a configuration',
          headers: 'Authorization: Token your-token',
        },
      ],
    },
    {
      category: 'Super Admin',
      items: [
        {
          method: 'GET',
          path: '/api/v1/global-settings/',
          description: 'Get global settings (IP allowlist/denylist)',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'PUT',
          path: '/api/v1/global-settings/1/',
          description: 'Update global settings',
          headers: 'Authorization: Token your-token',
          body: '{ "ip_whitelist": "1.2.3.4", "ip_denylist": "5.6.7.8" }',
        },
        {
          method: 'GET',
          path: '/api/v1/admin-users/',
          description: 'List admin users',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'POST',
          path: '/api/v1/admin-users/',
          description: 'Create admin user',
          headers: 'Authorization: Token your-token',
          body: '{ "username": "newuser", "password": "password", "email": "user@example.com", "is_superuser": false }',
        },
        {
          method: 'GET',
          path: '/api/v1/audit-logs/',
          description: 'Get audit logs',
          headers: 'Authorization: Token your-token',
        },
      ],
    },
    {
      category: 'PostgreSQL Status',
      items: [
        {
          method: 'GET',
          path: '/api/v1/postgres/status/',
          description: 'Get server version, uptime, database size, connection stats and configuration',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'GET',
          path: '/api/v1/postgres/tables/',
          description: 'List tables with sizes, row counts, and vacuum stats (params: page, sort, search)',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'GET',
          path: '/api/v1/postgres/activity/',
          description: 'Get active queries and connections from pg_stat_activity',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'POST',
          path: '/api/v1/postgres/query/',
          description: 'Execute a read-only SQL query (body: {"sql": "SELECT ..."}). Only SELECT statements allowed.',
          headers: 'Authorization: Token your-token',
          body: '{ "sql": "SELECT * FROM marketing_contact LIMIT 10" }',
          response: '{ "success": true, "columns": [...], "rows": [...], "row_count": N }',
        },
      ],
    },
    {
      category: 'Insights',
      items: [
        {
          method: 'GET',
          path: '/api/v1/tenants/',
          description: 'Tenant overview — lists all distinct tenants with resource counts (contacts, configs, webhooks, sync runs, audit logs), first seen/last active timestamps, and share percentages',
          headers: 'Authorization: Token your-token',
          response: '{ "tenants": [...], "total_tenants": N, "total_records": N }',
        },
        {
          method: 'GET',
          path: '/api/v1/analytics/',
          description: 'Usage analytics — time-series data for admin activity, contacts, sync runs, webhooks. Supports ?days= param (default 30, max 365). Tenant-scoped via X-Tenant-ID header.',
          headers: 'Authorization: Token your-token',
          response: '{ "period_days": 30, "audit_by_day": [...], "contacts_by_day": [...], ... }',
        },
        {
          method: 'GET',
          path: '/api/v1/health/',
          description: 'Health check — verifies database connectivity, Brevo API reachability, and configuration completeness. Returns overall status (healthy/degraded/unhealthy/not_configured).',
          headers: 'Authorization: Token your-token',
          response: '{ "overall": "healthy", "checks": { "database": {...}, "brevo_api": {...}, "configuration": {...} } }',
        },
      ],
    },
  ];

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'green';
      case 'POST': return 'blue';
      case 'PUT': return 'yellow';
      case 'DELETE': return 'red';
      default: return 'gray';
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>API Documentation</Heading>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Text mb={4} color="gray.600">
          This API documentation provides an overview of all available endpoints for the Marketing Brevo service.
        </Text>

        <Accordion allowMultiple>
          {endpoints.map((category, catIndex) => (
            <AccordionItem key={catIndex} border="none" mb={2}>
              <AccordionButton bg="gray.50" borderRadius="md" _hover={{ bg: 'gray.100' }}>
                <Box flex="1" textAlign="left" fontWeight="bold">
                  {category.category}
                </Box>
                <Badge colorScheme="orange" mr={2}>{category.items.length} endpoints</Badge>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <VStack spacing={4} align="stretch">
                  {category.items.map((endpoint, endIndex) => (
                    <Box key={endIndex} p={4} bg="gray.50" borderRadius="md">
                      <HStack mb={2}>
                        <Badge colorScheme={getMethodColor(endpoint.method)}>{endpoint.method}</Badge>
                        <Code bg="transparent" fontWeight="bold">{endpoint.path}</Code>
                      </HStack>
                      <Text fontSize="sm" color="gray.600" mb={2}>{endpoint.description}</Text>
                      {endpoint.headers && (
                        <Text fontSize="xs" fontFamily="mono" color="gray.500">
                          Headers: {endpoint.headers}
                        </Text>
                      )}
                      {endpoint.body && (
                        <Box mt={2}>
                          <Text fontSize="xs" color="gray.500" mb={1}>Request Body:</Text>
                          <Code fontSize="xs" p={2} display="block" whiteSpace="pre-wrap" bg="gray.100" borderRadius="md">
                            {endpoint.body}
                          </Code>
                        </Box>
                      )}
                      {endpoint.response && (
                        <Box mt={2}>
                          <Text fontSize="xs" color="gray.500" mb={1}>Response:</Text>
                          <Code fontSize="xs" p={2} display="block" whiteSpace="pre-wrap" bg="gray.100" borderRadius="md">
                            {endpoint.response}
                          </Code>
                        </Box>
                      )}
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
    </Box>
  );
}

export default ApiDocs;
